import { type FC, useCallback } from "react";
import classNames from "classnames";
import { Controller, FormProvider, useForm } from "react-hook-form";
import SearchPeriodSelector from "../../../OrdersQuotes/OrderListPage/components/OrderListSearchBox/SearchPeriodSelector/SearchPeriodSelector";
import { ABMonitorLineItemStatus, OrderLineStatusFilter } from "./components/OrderLineStatusFilter";
import { getPredefinedPeriodDates, PredefinedDate } from "../../../OrdersQuotes/common/utils/predefinedDates";
import { useOnMount } from "../../../common/hooks/useOnMount";
import {
  makeSearchByValidator,
  QueryType,
  type SearchByData,
  SearchByInput,
} from "./components/SearchByInput/SearchByInput";
import { Button } from "../../../common/components/Button/Button";
import { IconType } from "../../../common/components/icon/IconType";
import { isDateValidForOrderSearch } from "../../../OrdersQuotes/common/utils/IsDateValidForOrderSearch";
import classes from "./ABMonitorSearchBox.module.less";
import { SearchCard } from "../../../common/components/SearchCard";
import { isMultiAccountOrderSearchEnabled } from "../../../utils/features";
import {
  getInitialAccounts,
  MultiAccountSelect,
} from "../../../OrdersQuotes/OrderListPage/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSelect";
import { type SingleAccountData } from "../../../OrdersQuotes/common/DownloadOrderHistoryService/types";
import { useMultiAccountSearchData } from "~/OrdersQuotes/OrderListPage/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSearchDataProvider";
import {
  ABMonitorConfirmedDeliveryStatus,
  ConfirmedDeliveryLineFilter,
} from "~/ABMonitor/components/ABMonitorSearchBox/components/ConfirmedDeliveryLineFilter";
import { useTranslation } from "react-i18next";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";

interface Props {
  isLoading?: boolean;
  className?: string;
  onSubmit: (data: ABMonitorSearchBoxData) => void;
  disabled?: boolean;
}

export interface ABMonitorSearchBoxData {
  query: string;
  queryType: QueryType;
  dateFrom?: Date;
  dateTo?: Date;
  lineStatus?: string;
  accounts?: SingleAccountData[];
  confirmedLines: string;
  deliveryDate?: string;
}

export interface ABMonitorSearchFormData {
  chosenDate: { start: Date | undefined; end: Date | undefined };
  isCustomDate: boolean;
  isPredefinedDate: boolean;
  lineStatusOption: string;
  predefinedDate: PredefinedDate;
  searchBy: SearchByData;
  confirmedLines: string;
  accounts: string[];
}

const INITIAL_SEARCH_FORM_DATA: ABMonitorSearchFormData = {
  predefinedDate: PredefinedDate.LAST_30_DAYS,
  lineStatusOption: ABMonitorLineItemStatus.ALL,
  chosenDate: { start: undefined, end: undefined },
  isCustomDate: false,
  isPredefinedDate: true,
  searchBy: {
    query: "",
    type: QueryType.PURCHASE_NO,
  },
  accounts: getInitialAccounts(),
  confirmedLines: ABMonitorConfirmedDeliveryStatus.ALL,
};

const AVAILABLE_QUERY_TYPES = [
  QueryType.PURCHASE_NO,
  QueryType.SALES_NO,
  QueryType.DESIGNATION,
  QueryType.SAP_MATERIAL_NO,
  QueryType.CUSTOMER_MATERIAL_NO,
];

type GetDatesFromFormProp = Pick<ABMonitorSearchFormData, "isCustomDate" | "chosenDate" | "predefinedDate">;
export const getDatesFromForm = (form: GetDatesFromFormProp) => {
  // check if custom date is set
  if (form.isCustomDate) {
    return {
      dateFrom: form.chosenDate.start,
      dateTo: form.chosenDate.end,
    };
  } else {
    return getPredefinedPeriodDates(form.predefinedDate);
  }
};

const ABMonitorSearchBox: FC<Props> = ({ isLoading, onSubmit, className, disabled }) => {
  const { t } = useTranslation();
  const multiAccountData = useMultiAccountSearchData();

  // const showUnconfirmedLinesFilter = window.SCHA.abmonitor?.showUnconfirmedLinesFilter ?? false;
  //
  // console.log({showUnconfirmedLinesFilter});

  const formHooks = useForm({ defaultValues: INITIAL_SEARCH_FORM_DATA, mode: "onTouched" });

  const getValues = formHooks.getValues;

  const onSubmitCb = useCallback(() => {
    const formData = getValues() as ABMonitorSearchFormData;
    const { lineStatusOption, searchBy, accounts: formAccounts, confirmedLines } = formData;

    let accounts:
      | {
          accountNumber: string;
          endCustomer: string;
          salesOrg: string;
        }[]
      | undefined = undefined;
    if (formAccounts) {
      accounts = formAccounts
        .map((str) => str.split("-"))
        .map((parts) => ({
          accountNumber: parts[0],
          endCustomer: parts[1],
          salesOrg: parts[2],
        }));
    }

    const requestPayload: ABMonitorSearchBoxData = {
      query: searchBy.query,
      queryType: searchBy.type,
      lineStatus: lineStatusOption,
      ...getDatesFromForm(formData),
      accounts,
      confirmedLines: confirmedLines,
    };

    onSubmit(requestPayload);
  }, [getValues]);

  useOnMount(onSubmitCb);

  return (
    <FormProvider {...formHooks}>
      <SearchCard className={classNames(className, classes.card)} data-test-id={"abmonitor-search-box"}>
        <form onSubmit={formHooks.handleSubmit(onSubmitCb)}>
          <div className={classes.row}>
            <Controller
              render={({ field: { value, onChange, onBlur, ref }, fieldState: { error } }) => (
                <SearchByInput
                  className={classes.searchBy}
                  value={value}
                  onChange={onChange}
                  visibleOptions={AVAILABLE_QUERY_TYPES}
                  pageVariant={"abmonitor"}
                  error={!!error}
                  helperLabel={error?.message}
                  onBlur={onBlur}
                  ref={ref}
                />
              )}
              name={"searchBy"}
              defaultValue={INITIAL_SEARCH_FORM_DATA.searchBy}
              control={formHooks.control}
              rules={{
                validate: makeSearchByValidator(t, "abmonitor"),
              }}
            />

            <SearchPeriodSelector
              formHooks={formHooks}
              isDateValidForSelection={isDateValidForOrderSearch}
              className={classes.searchPeriod}
            />
          </div>

          <div className={classes.row}>
            <div className={classes.searchStatus}>
              <OrderLineStatusFilter />
            </div>

            {isMultiAccountOrderSearchEnabled() && (
              <div className={classes.accountSelect}>
                <Controller
                  render={({ field: { value, onChange } }) => <MultiAccountSelect values={value} onChange={onChange} />}
                  name={"accounts"}
                  defaultValue={INITIAL_SEARCH_FORM_DATA.accounts}
                  control={formHooks.control}
                />
              </div>
            )}

            {multiAccountData.showUnconfirmedLinesFilter && (
              <div className={classes.searchStatus}>
                <ConfirmedDeliveryLineFilter />
              </div>
            )}
          </div>

          <div className={classes.buttonRow}>
            <Button
              variant={"text"}
              data-test-id={"search-form-reset"}
              startIcon={<DefaultButtonIcon type={IconType.Reload} />}
              onClick={() => {
                formHooks.reset(INITIAL_SEARCH_FORM_DATA);
                onSubmitCb();
              }}
            >
              {t("common.button.resetForm")}
            </Button>

            <Button
              disabled={window.SCHA.config.disableAllWS || disabled || isLoading}
              type={"submit"}
              data-test-id={"abmonitor-search-submit"}
            >
              {t("quoteListPage.applyBtn")}
            </Button>
          </div>
        </form>
      </SearchCard>
    </FormProvider>
  );
};

export default ABMonitorSearchBox;
